import {
  RadialDuctMetricsType,
  RadialDuctRoomMetricsType,
  RadialDuctRoomSupplyRunMetricsType,
  RadialDuctZoneReturnRunMetricsType
} from 'data/types';
import {
  PlenumOpeningDimensionsType,
  ZoneDuctPlenumNeedType
} from 'utils/concealed-plenum-dimensions';

const SUPPLY_REGISTER_MAX_FACE_FPM = 600;
const RETURN_GRILLE_MAX_FACE_FPM = 425;

const REGISTER_TABLE = [
  { size: '4×8', nfa: 0.155 },
  { size: '6×6', nfa: 0.175 },
  { size: '4×10', nfa: 0.195 },
  { size: '4×12', nfa: 0.233 },
  { size: '4×14', nfa: 0.272 },
  { size: '6×10', nfa: 0.292 },
  { size: '8×8', nfa: 0.311 },
  { size: '6×12', nfa: 0.35 },
  { size: '8×10', nfa: 0.389 },
  { size: '6×14', nfa: 0.408 },
  { size: '8×12', nfa: 0.467 },
  { size: '10×10', nfa: 0.486 },
  { size: '8×14', nfa: 0.545 },
  { size: '10×12', nfa: 0.583 },
  { size: '10×14', nfa: 0.68 },
  { size: '12×12', nfa: 0.7 },
  { size: '12×14', nfa: 0.817 },
  { size: '14×14', nfa: 0.953 }
] as const;

const GRILLE_TABLE = [
  { size: '10×10', nfa: 0.486 },
  { size: '12×12', nfa: 0.7 },
  { size: '14×14', nfa: 0.953 },
  { size: '20×10', nfa: 0.972 },
  { size: '20×12', nfa: 1.167 },
  { size: '16×16', nfa: 1.244 },
  { size: '20×14', nfa: 1.361 },
  { size: '24×12', nfa: 1.4 },
  { size: '30×10', nfa: 1.458 },
  { size: '24×14', nfa: 1.633 },
  { size: '30×12', nfa: 1.75 },
  { size: '20×20', nfa: 1.944 },
  { size: '30×14', nfa: 2.042 },
  { size: '24×24', nfa: 2.8 }
] as const;

export type DuctSupplyLineKindType =
  | 'metal_duct'
  | 'flex_duct'
  | 'adapter'
  | 'elbow'
  | 'register'
  | 'boot'
  | 'return_grille'
  | 'grille_box'
  | 'reducer';

export type DuctSupplyLineStatusType = 'found' | 'missing';

export type DuctSupplyLineType = {
  quantity: string;
  label: string;
  kind?: DuctSupplyLineKindType;
  sizeIn?: number;
  status?: DuctSupplyLineStatusType;
};

export type DuctRunSuppliesType = {
  key: string;
  heading: string;
  lines: DuctSupplyLineType[];
  coveredByLabel: string | null;
};

export type ZoneDuctSuppliesType = {
  supplyRuns: DuctRunSuppliesType[];
  returnRuns: DuctRunSuppliesType[];
  lineCount: number;
  neededLineCount: number;
};

export type ZoneDuctSuppliesCoverageType = {
  supplyCoveredByLabel: string | null;
  returnCoveredByLabel: string | null;
};

export type DuctSuppliesModeType = 'flex_only' | 'metal_flex' | 'metal_only';

export const FLEX_TAIL_LENGTH_FT = 6;

export const RETURN_REDUCER_LARGE_IN = 10;
export const RETURN_REDUCER_SMALL_IN = 9;

type GrilleEntry = { size: string; nfa: number };

function pickByFreeArea(
  table: ReadonlyArray<GrilleEntry>,
  cfm: number,
  maxFaceFpm: number
): { entry: GrilleEntry | null; faceFpm: number | null } {
  const requiredNFA = cfm / maxFaceFpm;
  const entry = table.find((option) => option.nfa >= requiredNFA) ?? null;
  return { entry, faceFpm: entry ? cfm / entry.nfa : null };
}

function runHeading(
  name: string,
  run: { cfm: number; autoSizeIn: number | null; velocityFpm: number | null }
): string {
  const size = run.autoSizeIn !== null ? `${run.autoSizeIn}"` : '—';
  const velocity =
    run.velocityFpm !== null ? ` (${run.velocityFpm.toFixed(0)} FPM)` : '';
  return `${name} — ${run.cfm.toFixed(0)} CFM @ ${size} Duct${velocity}`;
}

function pluralize(label: string, count: number): string {
  return count === 1 ? label : `${label}s`;
}

function buildFlexSupplyDuctLines(
  run: RadialDuctRoomSupplyRunMetricsType,
  size: number
): DuctSupplyLineType[] {
  const runLengthFt = Math.ceil(Math.max(run.ductLengthFt, 0));
  if (runLengthFt <= 0) return [];
  return [
    {
      quantity: `${runLengthFt}'`,
      label: `${size}" Metalized Flex Supply Duct`,
      kind: 'flex_duct',
      sizeIn: size
    }
  ];
}

function buildMetalOnlySupplyDuctLines(
  run: RadialDuctRoomSupplyRunMetricsType,
  size: number
): DuctSupplyLineType[] {
  const lines: DuctSupplyLineType[] = [];
  const metalLengthFt = Math.ceil(Math.max(run.ductLengthFt, 0));
  if (metalLengthFt > 0) {
    lines.push({
      quantity: `${metalLengthFt}'`,
      label: `${size}" Metal Supply Duct`,
      kind: 'metal_duct',
      sizeIn: size
    });
  }
  if (run.elbowCount > 0) {
    lines.push({
      quantity: `${run.elbowCount}`,
      label: pluralize(`${size}" 90° Elbow`, run.elbowCount),
      kind: 'elbow',
      sizeIn: size
    });
  }
  return lines;
}

function buildMetalFlexSupplyDuctLines(
  run: RadialDuctRoomSupplyRunMetricsType,
  size: number
): DuctSupplyLineType[] {
  const lines: DuctSupplyLineType[] = [];
  const tailCount = run.flexTailCount;
  const runLengthFt = Math.max(run.ductLengthFt, 0);
  const flexLengthFt = Math.min(tailCount * FLEX_TAIL_LENGTH_FT, runLengthFt);
  const metalLengthFt = Math.ceil(runLengthFt - flexLengthFt);
  if (metalLengthFt > 0) {
    lines.push({
      quantity: `${metalLengthFt}'`,
      label: `${size}" Metal Supply Duct`,
      kind: 'metal_duct',
      sizeIn: size
    });
  }
  if (tailCount > 0 && flexLengthFt > 0) {
    lines.push({
      quantity: `${Math.ceil(flexLengthFt)}'`,
      label: `${size}" Flex Duct (tail)`,
      kind: 'flex_duct',
      sizeIn: size
    });
    lines.push({
      quantity: `${tailCount}`,
      label: pluralize(`${size}" Metal-to-Flex Adapter`, tailCount),
      kind: 'adapter',
      sizeIn: size
    });
  }
  if (run.elbowCount > 0) {
    lines.push({
      quantity: `${run.elbowCount}`,
      label: pluralize(`${size}" 90° Elbow`, run.elbowCount),
      kind: 'elbow',
      sizeIn: size
    });
  }
  return lines;
}

function buildSupplyRunLines(
  run: RadialDuctRoomSupplyRunMetricsType,
  mode: DuctSuppliesModeType
): DuctSupplyLineType[] {
  const size = run.autoSizeIn;
  if (size === null) {
    return [{ quantity: '—', label: 'No stocked duct size fits this run' }];
  }

  let lines: DuctSupplyLineType[];
  if (mode === 'metal_flex') {
    lines = buildMetalFlexSupplyDuctLines(run, size);
  } else if (mode === 'metal_only') {
    lines = buildMetalOnlySupplyDuctLines(run, size);
  } else {
    lines = buildFlexSupplyDuctLines(run, size);
  }

  const register = pickByFreeArea(
    REGISTER_TABLE,
    run.cfm,
    SUPPLY_REGISTER_MAX_FACE_FPM
  );
  if (register.entry) {
    lines.push({
      quantity: '1',
      label: `${register.entry.size} Register (${register.faceFpm?.toFixed(
        0
      )} FPM)`,
      kind: 'register',
      sizeIn: size
    });
  } else {
    lines.push({
      quantity: '1',
      label: `Register — none fits (needs ${(
        run.cfm / SUPPLY_REGISTER_MAX_FACE_FPM
      ).toFixed(2)} sq ft free area)`
    });
  }
  lines.push({
    quantity: '1',
    label: `${size}" to Register Boot`,
    kind: 'boot',
    sizeIn: size
  });

  return lines;
}

function buildReturnRunLines(
  run: RadialDuctZoneReturnRunMetricsType,
  mode: DuctSuppliesModeType
): DuctSupplyLineType[] {
  const size = run.autoSizeIn;
  const isMetalReturn = mode !== 'flex_only';
  const ductLabel = isMetalReturn
    ? `${size}" Metal Return Duct`
    : `${size}" Metalized Flex Return Duct`;
  const lines: DuctSupplyLineType[] = [];
  lines.push({
    quantity: `${Math.ceil(Math.max(run.ductLengthFt, 0))}'`,
    label: ductLabel,
    kind: isMetalReturn ? 'metal_duct' : 'flex_duct',
    sizeIn: size
  });
  if (isMetalReturn && run.elbowCount > 0) {
    lines.push({
      quantity: `${run.elbowCount}`,
      label: pluralize(`${size}" 90° Elbow`, run.elbowCount),
      kind: 'elbow',
      sizeIn: size
    });
  }

  const grille = pickByFreeArea(
    GRILLE_TABLE,
    run.cfm,
    RETURN_GRILLE_MAX_FACE_FPM
  );
  if (grille.entry) {
    lines.push({
      quantity: '1',
      label: `${grille.entry.size} Return Grille (${grille.faceFpm?.toFixed(
        0
      )} FPM)`,
      kind: 'return_grille',
      sizeIn: size
    });
  } else {
    lines.push({
      quantity: '1',
      label: `Return Grille — none fits (needs ${(
        run.cfm / RETURN_GRILLE_MAX_FACE_FPM
      ).toFixed(2)} sq ft free area)`
    });
  }
  const usesReducer = !isMetalReturn && size === RETURN_REDUCER_SMALL_IN;
  const boxSizeIn = usesReducer ? RETURN_REDUCER_LARGE_IN : size;
  lines.push({
    quantity: '1',
    label: `${boxSizeIn}" to Grille Adapter/Filter Box`,
    kind: 'grille_box',
    sizeIn: boxSizeIn
  });
  if (usesReducer) {
    lines.push({
      quantity: '1',
      label: `${RETURN_REDUCER_LARGE_IN}" × ${RETURN_REDUCER_SMALL_IN}" Round Reducer`,
      kind: 'reducer',
      sizeIn: RETURN_REDUCER_SMALL_IN
    });
  }

  return lines;
}

function roomRunName(
  room: RadialDuctRoomMetricsType,
  runIndex: number,
  runTotal: number,
  roomNamesByUniqueId: Map<string, string>
): string {
  const baseName = roomNamesByUniqueId.get(room.uniqueId) ?? 'Room';
  return runTotal > 1 ? `${baseName} (run ${runIndex + 1})` : baseName;
}

function formatPlenumInches(value: number): string {
  return `${Number(value.toFixed(3))}"`;
}

function plenumRunGroup(
  side: 'Supply' | 'Return',
  dimensions: PlenumOpeningDimensionsType | null
): DuctRunSuppliesType {
  const sizeText = dimensions
    ? `${formatPlenumInches(dimensions.widthIn)} × ${formatPlenumInches(
        dimensions.heightIn
      )} opening`
    : 'sized to unit opening';
  return {
    key: `plenum-${side.toLowerCase()}`,
    heading: `${side} Plenum — ${sizeText}`,
    coveredByLabel: null,
    lines: [
      {
        quantity: '1',
        label: `${side} Plenum (slip-over, ${sizeText})`,
        status: 'missing'
      }
    ]
  };
}

export function buildZoneDuctSupplies(
  metrics: RadialDuctMetricsType,
  roomNamesByUniqueId: Map<string, string>,
  coverage?: ZoneDuctSuppliesCoverageType,
  mode: DuctSuppliesModeType = 'flex_only',
  plenumNeed?: ZoneDuctPlenumNeedType | null
): ZoneDuctSuppliesType {
  const supplyCoveredByLabel = coverage?.supplyCoveredByLabel ?? null;
  const returnCoveredByLabel = coverage?.returnCoveredByLabel ?? null;

  const supplyRuns = [
    ...metrics.rooms.flatMap((room) =>
      room.supplies.map((run, runIndex) => ({
        key: `supply-${room.uniqueId}-${runIndex}`,
        heading: runHeading(
          roomRunName(
            room,
            runIndex,
            room.supplies.length,
            roomNamesByUniqueId
          ),
          run
        ),
        lines: buildSupplyRunLines(run, mode),
        coveredByLabel: supplyCoveredByLabel
      }))
    ),
    ...(plenumNeed ? [plenumRunGroup('Supply', plenumNeed.supply)] : [])
  ];

  const returns = metrics.returnRun?.returns ?? [];
  const returnRuns = [
    ...returns.map((run, index) => ({
      key: `return-${index}`,
      heading: runHeading(
        returns.length > 1 ? `Return ${index + 1}` : 'Return',
        run
      ),
      lines: buildReturnRunLines(run, mode),
      coveredByLabel: returnCoveredByLabel
    })),
    ...(plenumNeed ? [plenumRunGroup('Return', plenumNeed.return)] : [])
  ];

  const allRuns = [...supplyRuns, ...returnRuns];
  const lineCount = allRuns.reduce((total, run) => total + run.lines.length, 0);
  const neededLineCount = allRuns.reduce(
    (total, run) =>
      run.coveredByLabel === null ? total + run.lines.length : total,
    0
  );

  return { supplyRuns, returnRuns, lineCount, neededLineCount };
}
